/**
 * Unified Resource Input Bar - Alpine.js Component
 *
 * Single chat-like input bar that auto-detects input type:
 * - YouTube video URLs
 * - YouTube channel URLs
 * - PDF files (via drag & drop or file picker)
 * - Video files (MP4/WEBM via GCS, others via Livewire)
 *
 * Supports multi-file upload with a concurrent queue system.
 */

import { GcsUploader } from './video-uploader';

// File size limits
const PDF_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const LEGACY_VIDEO_MAX_SIZE = 100 * 1024 * 1024; // 100MB for extended formats

// Primary video formats (GCS upload)
const GCS_VIDEO_TYPES = ['video/mp4', 'video/webm'];
const GCS_VIDEO_EXTENSIONS = ['mp4', 'webm'];

// Extended video formats (Livewire upload)
const LEGACY_VIDEO_TYPES = [
    'video/quicktime', 'video/x-msvideo', 'video/x-matroska',
    'video/x-ms-wmv', 'video/x-flv', 'video/3gpp', 'video/mpeg',
    'video/mp2t', 'video/x-m4v', 'video/ogg'
];
const LEGACY_VIDEO_EXTENSIONS = ['mov', 'avi', 'mkv', 'wmv', 'flv', '3gp', 'mpeg', 'mpg', 'ts', 'm4v', 'mts', 'ogv'];

// Upload queue concurrency
const MAX_CONCURRENT = 2;
let fileIdCounter = 0;

// YouTube URL patterns
const YOUTUBE_VIDEO_PATTERNS = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/,
    /youtube\.com\/embed\/([\w-]+)/,
    /youtube\.com\/v\/([\w-]+)/,
    /youtube\.com\/shorts\/([\w-]+)/,
];

const YOUTUBE_CHANNEL_PATTERNS = [
    /youtube\.com\/@[\w-]+/,
    /youtube\.com\/channel\/[\w-]+/,
    /youtube\.com\/c\/[\w-]+/,
    /youtube\.com\/user\/[\w-]+/,
];

/**
 * Alpine.js component for unified resource input
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('resourceInputBar', () => ({
        // State
        inputMode: 'idle',           // 'idle' | 'url' | 'file'
        detectedType: null,          // 'youtube_video' | 'youtube_channel' | 'pdf' | 'video_gcs' | 'video_legacy'
        youtubeScope: 'video',       // 'video' | 'channel' — what a detected YouTube link adds
        hasYoutube: true,
        dragOver: false,
        uploading: false,
        error: null,
        url: '',
        files: [],
        ragId: null,
        maxFileSize: 500 * 1024 * 1024, // Default 500MB

        updateRagId() {
            this.ragId = this.$event.target.value;
        },

        // Initialize
        init() {
            this.ragId = parseInt(this.$el.dataset.ragId) || null;
            this.maxFileSize = parseInt(this.$el.dataset.maxFileSize) || (500 * 1024 * 1024);
            this.hasYoutube = this.$el.dataset.hasYoutube === '1';
            // Listen for video upload success event
            window.addEventListener('video-uploaded', (event) => {
                this.$wire.dispatch('newMessage');
                this.$wire.dispatch('update_stats_box');
                this.$wire.dispatch('pg:eventRefresh-default');
            });
        },

        // ==================== File Entry Factory ====================

        /**
         * Create a file queue entry
         */
        createFileEntry(file, type) {
            return {
                id: ++fileIdCounter,
                file: file,
                type: type,
                status: 'queued',
                progress: 0,
                error: null,
                uploader: null,
                uploadId: null,
                bytesUploaded: 0,
                totalBytes: file.size,
                uploadSpeed: 0,
                timeRemaining: 0,
            };
        },

        // ==================== URL Detection ====================

        /**
         * Handle paste event - detect YouTube URLs from clipboard
         */
        handlePaste(event) {
            // Let the paste happen, then check the value
            setTimeout(() => {
                this.handleInput();
            }, 0);
        },

        /**
         * Handle input changes - real-time URL type detection
         */
        handleInput() {
            const value = this.url.trim();

            if (!value) {
                this.inputMode = 'idle';
                this.detectedType = null;
                this.error = null;
                return;
            }

            this.inputMode = 'url';
            this.detectedType = this.detectUrlType(value);
            this.error = null;

            // Preselect the obvious scope so Enter always does something sensible.
            if (this.detectedType === 'youtube_channel') {
                this.youtubeScope = 'channel';
            } else if (this.detectedType === 'youtube_video') {
                this.youtubeScope = 'video';
            }
        },

        /**
         * Detect URL type from string
         * @returns 'youtube_video' | 'youtube_channel' | null
         */
        detectUrlType(url) {
            // Check YouTube video patterns
            for (const pattern of YOUTUBE_VIDEO_PATTERNS) {
                if (pattern.test(url)) {
                    return 'youtube_video';
                }
            }

            // Check YouTube channel patterns
            for (const pattern of YOUTUBE_CHANNEL_PATTERNS) {
                if (pattern.test(url)) {
                    return 'youtube_channel';
                }
            }

            return null;
        },

        // ==================== File Handling ====================

        /**
         * Handle drag over event
         */
        handleDragOver(event) {
            event.preventDefault();
            this.dragOver = true;
        },

        /**
         * Handle drag leave event
         */
        handleDragLeave(event) {
            event.preventDefault();
            this.dragOver = false;
        },

        /**
         * Handle file drop event (multi-file)
         */
        handleDrop(event) {
            event.preventDefault();
            this.dragOver = false;

            const droppedFiles = event.dataTransfer?.files;
            if (droppedFiles && droppedFiles.length > 0) {
                this.processFiles(Array.from(droppedFiles));
            }
        },

        /**
         * Handle file picker selection (multi-file)
         */
        handleFileSelect(event) {
            const selectedFiles = event.target.files;
            if (selectedFiles && selectedFiles.length > 0) {
                this.processFiles(Array.from(selectedFiles));
            }
            event.target.value = '';
        },

        /**
         * Process selected/dropped files into the queue
         */
        processFiles(fileList) {
            this.error = null;

            for (const file of fileList) {
                const fileType = this.detectFileType(file);

                if (!fileType) {
                    this.files.push({
                        ...this.createFileEntry(file, null),
                        status: 'error',
                        error: 'Unsupported file type',
                    });
                    continue;
                }

                const sizeLimit = this.getFileSizeLimit(fileType);
                if (file.size > sizeLimit) {
                    this.files.push({
                        ...this.createFileEntry(file, fileType),
                        status: 'error',
                        error: `Exceeds ${this.formatFileSize(sizeLimit)} limit (${this.formatFileSize(file.size)})`,
                    });
                    continue;
                }

                this.files.push(this.createFileEntry(file, fileType));
            }

            if (this.files.length > 0) {
                this.inputMode = 'file';
            }
        },

        /**
         * Remove a file from the queue (cancel if uploading)
         */
        removeFile(fileId) {
            const entry = this.files.find(f => f.id === fileId);
            if (!entry) return;

            if (entry.status === 'uploading' && entry.uploader) {
                entry.uploader.cancel();
                if (entry.uploadId) {
                    this.cancelFileUpload(entry);
                }
            }

            this.files = this.files.filter(f => f.id !== fileId);

            if (this.files.length === 0) {
                this.inputMode = 'idle';
                this.uploading = false;
            }
        },

        /**
         * Detect file type from File object
         * @returns 'pdf' | 'video_gcs' | 'video_legacy' | null
         */
        detectFileType(file) {
            const mimeType = file.type;
            const extension = file.name.split('.').pop()?.toLowerCase();

            // Check PDF
            if (mimeType === 'application/pdf' || extension === 'pdf') {
                return 'pdf';
            }

            // Check GCS video types (MP4/WEBM)
            if (GCS_VIDEO_TYPES.includes(mimeType) || GCS_VIDEO_EXTENSIONS.includes(extension)) {
                return 'video_gcs';
            }

            // Check legacy video types
            if (LEGACY_VIDEO_TYPES.includes(mimeType) || LEGACY_VIDEO_EXTENSIONS.includes(extension)) {
                return 'video_legacy';
            }

            return null;
        },

        /**
         * Get file size limit based on file type
         */
        getFileSizeLimit(fileType) {
            switch (fileType) {
                case 'pdf':
                    return PDF_MAX_SIZE;
                case 'video_gcs':
                    return this.maxFileSize;
                case 'video_legacy':
                    return LEGACY_VIDEO_MAX_SIZE;
                default:
                    return 0;
            }
        },

        // ==================== Submit Handler ====================

        /**
         * Main submit handler - routes to appropriate upload method
         */
        async submitResource() {
            if (this.uploading) return;

            // Handle file uploads
            if (this.files.length > 0 && this.files.some(f => f.status === 'queued')) {
                this.uploading = true;
                this.error = null;
                this.startNextUploads();
                return;
            }

            // Handle URL submission
            if (this.url && this.detectedType) {
                switch (this.detectedType) {
                    case 'youtube_video':
                    case 'youtube_channel':
                        if (!this.hasYoutube) {
                            this.error = 'YouTube links cannot be added here.';
                            return;
                        }
                        await (this.youtubeScope === 'channel'
                            ? this.submitAsChannel()
                            : this.submitYoutubeVideo());
                        break;
                    default:
                        this.error = 'Please enter a valid YouTube URL.';
                }
                return;
            }

            if (!this.url && this.files.length === 0) {
                this.error = 'Please enter a YouTube URL or select a file.';
            } else if (this.url && !this.detectedType) {
                this.error = 'Please enter a valid YouTube URL.';
            }
        },

        /**
         * Submit YouTube video URL via Livewire
         */
        async submitYoutubeVideo() {
            this.uploading = true;
            this.error = null;

            try {
                await this.$wire.set('url', this.url);
                await this.$wire.createSingleVideo();
                this.resetState();
            } catch (error) {
                this.error = error.message || 'Failed to add video';
            } finally {
                this.uploading = false;
            }
        },

        /**
         * Submit as channel (for channel URLs)
         */
        async submitAsChannel() {
            this.uploading = true;
            this.error = null;

            try {
                await this.$wire.set('url', this.url);
                await this.$wire.createChannel();
                this.resetState();
            } catch (error) {
                this.error = error.message || 'Failed to add channel';
            } finally {
                this.uploading = false;
            }
        },

        // ==================== Queue Management ====================

        /**
         * Start next uploads up to MAX_CONCURRENT limit
         */
        startNextUploads() {
            const inProgress = this.files.filter(f => f.status === 'uploading');
            const queued = this.files.filter(f => f.status === 'queued');

            if (queued.length === 0 && inProgress.length === 0) {
                this.uploading = false;
                this.dispatchCompletionEvents();
                return;
            }

            const livewireInProgress = inProgress.some(f => f.type === 'pdf' || f.type === 'video_legacy');

            let slotsAvailable = MAX_CONCURRENT - inProgress.length;

            for (const entry of queued) {
                if (slotsAvailable <= 0) break;

                const isLivewire = entry.type === 'pdf' || entry.type === 'video_legacy';
                if (isLivewire && livewireInProgress) continue;

                this.uploadFile(entry);
                slotsAvailable--;
                if (isLivewire) break;
            }
        },

        /**
         * Dispatch events after all uploads complete
         */
        dispatchCompletionEvents() {
            this.$wire.dispatch('newMessage');
            this.$wire.dispatch('update_stats_box');
            this.$wire.dispatch('pg:eventRefresh-default');
        },

        /**
         * Upload a single file entry (dispatch by type)
         */
        async uploadFile(entry) {
            entry.status = 'uploading';

            try {
                switch (entry.type) {
                    case 'pdf':
                        await this.uploadPdfEntry(entry);
                        break;
                    case 'video_gcs':
                        await this.uploadGcsEntry(entry);
                        break;
                    case 'video_legacy':
                        await this.uploadLegacyVideoEntry(entry);
                        break;
                }
            } catch (error) {
                entry.status = 'error';
                entry.error = error.message || 'Upload failed';
                this.onFileComplete();
            }
        },

        /**
         * Called when a file finishes (success or error) to advance the queue
         */
        onFileComplete() {
            this.startNextUploads();
        },

        // ==================== File Uploads ====================

        /**
         * Upload PDF via Livewire file upload
         */
        async uploadPdfEntry(entry) {
            return new Promise((resolve, reject) => {
                this.$wire.upload('pdfFile', entry.file, () => {
                    this.$wire.uploadPdf();
                    entry.progress = 100;
                    entry.status = 'complete';
                    this.onFileComplete();
                    resolve();
                }, (error) => {
                    entry.status = 'error';
                    entry.error = 'Failed to upload: ' + error;
                    this.onFileComplete();
                    reject(new Error(error));
                }, (event) => {
                    entry.progress = event.detail.progress;
                });
            });
        },

        /**
         * Upload video via GCS resumable upload (MP4/WEBM)
         */
        async uploadGcsEntry(entry) {
            const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;
            if (!csrfToken) {
                throw new Error('CSRF token not found');
            }

            const initResponse = await fetch('/api/video-upload/init', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    filename: entry.file.name,
                    mime_type: entry.file.type,
                    file_size: entry.file.size,
                    rag_id: this.ragId,
                }),
            });

            if (!initResponse.ok) {
                let errorMessage = 'Failed to initialize upload';
                try {
                    const errorData = await initResponse.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (parseError) {
                    if (initResponse.status === 429) {
                        errorMessage = 'Upload rate limited. Try again in a moment.';
                    } else if (initResponse.status >= 500) {
                        errorMessage = 'Server error. Please try again later.';
                    }
                }
                throw new Error(errorMessage);
            }

            const { upload_url, upload_id } = await initResponse.json();
            entry.uploadId = upload_id;

            entry.uploader = new GcsUploader();

            entry.uploader.addEventListener('progress', (e) => {
                entry.bytesUploaded = e.detail.bytesUploaded;
                entry.progress = parseFloat(e.detail.percent);
                entry.uploadSpeed = e.detail.speed;
                entry.timeRemaining = e.detail.timeRemaining;
            });

            entry.uploader.addEventListener('complete', async (e) => {
                await this.completeGcsEntryUpload(entry, e.detail.uploadId);
            });

            entry.uploader.addEventListener('error', (e) => {
                entry.status = 'error';
                entry.error = e.detail.error;
                this.onFileComplete();
            });

            entry.uploader.addEventListener('cancelled', () => {
                this.removeFile(entry.id);
            });

            await entry.uploader.initUpload(entry.file, upload_url, upload_id);
            await entry.uploader.upload();
        },

        /**
         * Complete GCS upload - notify backend
         */
        async completeGcsEntryUpload(entry, uploadId) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;

                const response = await fetch('/api/video-upload/complete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ upload_id: uploadId }),
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || 'Failed to complete upload');
                }

                entry.progress = 100;
                entry.status = 'complete';

                window.dispatchEvent(new CustomEvent('video-uploaded', {
                    detail: { videoUuid: result.video_uuid }
                }));
            } catch (error) {
                entry.status = 'error';
                entry.error = error.message;
            }

            this.onFileComplete();
        },

        /**
         * Cancel a file's GCS upload on the backend
         */
        async cancelFileUpload(entry) {
            if (entry.uploadId) {
                try {
                    const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content;
                    await fetch('/api/video-upload/cancel', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': csrfToken,
                        },
                        credentials: 'same-origin',
                        body: JSON.stringify({ upload_id: entry.uploadId }),
                    });
                } catch (error) {
                    console.error('Cancel upload error:', error);
                }
            }
        },

        /**
         * Upload legacy video format via Livewire
         */
        async uploadLegacyVideoEntry(entry) {
            return new Promise((resolve, reject) => {
                this.$wire.upload('videoFile', entry.file, () => {
                    this.$wire.uploadVideo();
                    entry.progress = 100;
                    entry.status = 'complete';
                    this.onFileComplete();
                    resolve();
                }, (error) => {
                    entry.status = 'error';
                    entry.error = 'Failed to upload: ' + error;
                    this.onFileComplete();
                    reject(new Error(error));
                }, (event) => {
                    entry.progress = event.detail.progress;
                });
            });
        },

        // ==================== Summary Helpers ====================

        /**
         * Get summary of queued files for display
         */
        getFilesSummary() {
            const queued = this.files.filter(f => f.status === 'queued');
            const pdfs = queued.filter(f => f.type === 'pdf').length;
            const videos = queued.filter(f => f.type === 'video_gcs' || f.type === 'video_legacy').length;
            const errors = this.files.filter(f => f.status === 'error').length;

            const parts = [];
            if (pdfs > 0) parts.push(pdfs + ' PDF' + (pdfs !== 1 ? 's' : ''));
            if (videos > 0) parts.push(videos + ' video' + (videos !== 1 ? 's' : ''));
            if (errors > 0) parts.push(errors + ' failed');

            return parts.join(', ') || this.files.length + ' files';
        },

        /**
         * Get upload progress summary for display
         */
        getUploadSummary() {
            const complete = this.files.filter(f => f.status === 'complete').length;
            const total = this.files.filter(f => f.status !== 'error').length;
            const inProgress = this.files.filter(f => f.status === 'uploading').length;

            if (inProgress > 0) {
                return `Uploading... ${complete}/${total} complete`;
            }
            return `${complete}/${total} complete`;
        },

        // ==================== Utility Methods ====================

        /**
         * Reset component state
         */
        resetState() {
            this.inputMode = 'idle';
            this.detectedType = null;
            this.youtubeScope = 'video';
            this.dragOver = false;
            this.uploading = false;
            this.error = null;
            this.url = '';
            this.files = [];

            const fileInput = this.$refs.fileInput;
            if (fileInput) {
                fileInput.value = '';
            }
        },

        /**
         * Format file size for display
         */
        formatFileSize(bytes) {
            if (bytes >= 1073741824) {
                return (bytes / 1073741824).toFixed(2) + ' GB';
            } else if (bytes >= 1048576) {
                return (bytes / 1048576).toFixed(0) + ' MB';
            } else if (bytes >= 1024) {
                return (bytes / 1024).toFixed(0) + ' KB';
            }
            return bytes + ' bytes';
        },

        /**
         * Format upload speed
         */
        formatSpeed(bytesPerSecond) {
            return this.formatFileSize(bytesPerSecond) + '/s';
        },

        /**
         * Format time remaining
         */
        formatTimeRemaining(seconds) {
            if (seconds < 60) {
                return Math.round(seconds) + 's';
            } else if (seconds < 3600) {
                return Math.floor(seconds / 60) + 'm ' + Math.round(seconds % 60) + 's';
            } else {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return hours + 'h ' + minutes + 'm';
            }
        },

        /**
         * Get detected type label for display
         */
        getDetectedTypeLabel() {
            switch (this.detectedType) {
                case 'youtube_video':
                    return 'YouTube Video';
                case 'youtube_channel':
                    return 'YouTube Channel';
                case 'pdf':
                    return 'PDF Document';
                case 'video_gcs':
                    return 'Video (MP4/WEBM)';
                case 'video_legacy':
                    return 'Video (Extended Format)';
                default:
                    return '';
            }
        },

        /**
         * Get detected type icon class
         */
        getDetectedTypeIcon() {
            switch (this.detectedType) {
                case 'youtube_video':
                case 'youtube_channel':
                    return 'fa-brands fa-youtube';
                case 'pdf':
                    return 'fa-solid fa-file-pdf';
                case 'video_gcs':
                case 'video_legacy':
                    return 'fa-solid fa-video';
                default:
                    return '';
            }
        },
    }));
});
